import {
  Accordion,
  AccordionSummary,
  Avatar,
  Box,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  ListItem,
  ListItemIcon,
  TextField,
  Typography,
} from '@mui/material';
import { DICTIONARY } from 'Enum';
import { Dictionary } from 'Dictionary';
import {
  PlusPurple,
  MinusPurple,
  AccordionArrowPurple,
  AccordionArrowPurpleClose,
  PurpleUnchecked,
  PurpleChecked,
} from 'Images';
import { useIsDesktop } from 'Hooks';
import React, { useEffect, useState } from 'react';
import { IEquipmentItem } from 'API';
import { Details } from './Details';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';

interface IEquipmentRowItem {
  item: IEquipmentItem;
  handleCheckItem: (checked: boolean) => void;
  handleToggle: (e: React.MouseEvent) => void;
  index: number;
  checked: boolean;
  period: number;
}
export interface IEquipmentOrderItem {
  itemNote: string;
  quantity: number;
  quantityNote: string;
}

export const RowProduct = ({
  handleToggle,
  handleCheckItem,
  checked,
  item,
  index,
  period,
}: IEquipmentRowItem) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [equipmentItem, setEquipmentItem] = useState<IEquipmentOrderItem>({
    itemNote: '',
    quantity: item.maxAVGQuantityPerMonth,
    quantityNote: '',
  });

  const isDesktop = useIsDesktop();
  const dictionary = Dictionary[DICTIONARY.Equipment].createOrder;

  useEffect(() => {
    setEquipmentItem((prevItem) => ({
      ...prevItem,
      quantity: item.quantity || item.maxAVGQuantityPerMonth * period,
    }));
  }, [item.maxAVGQuantityPerMonth, item.quantity, period]);

  const handleIncreaseQuantity = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      equipmentItem.quantity + 1 >=
      item.maxAVGQuantityPerMonth * (period || 1)
    ) {
      setIsExpanded(true);
    }
    setEquipmentItem({
      ...equipmentItem,
      quantity:
        equipmentItem.quantity + 1 > 0
          ? Math.min(equipmentItem.quantity + 1, 9999)
          : 1,
    });
  };

  const handleDecreaseQuantity = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEquipmentItem({
      ...equipmentItem,
      quantity:
        equipmentItem.quantity - 1 > 0
          ? Math.min(equipmentItem.quantity - 1, 9999)
          : 1,
    });
  };

  const handleChangeQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setEquipmentItem({
      ...equipmentItem,
      quantity:
        parseInt(e.target.value) > 0
          ? Math.min(parseInt(e.target.value), 9999)
          : 1,
    });
    if (
      parseInt(e.target.value) >
      item.maxAVGQuantityPerMonth * (period || 1)
    ) {
      setIsExpanded(true);
    }
  };

  return (
    <ListItem sx={{ p: 0, mb: 2 }}>
      {isDesktop ? (
        <Accordion
          tabIndex={0}
          role='group'
          aria-label={item.itemName}
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          onKeyDown={(e) => onEnterOrEscapeKeyDown(e)}
          expanded={isExpanded}
          sx={{
            width: '100%',
            boxShadow: '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)',
            borderRadius: '16px !important',
            border: '1px solid',
            borderColor: 'primary.shade_200',
          }}
          disableGutters
        >
          <AccordionSummary
            onClick={() => handleCheckItem(true)}
            sx={{
              pt: 3,
              pb: '21px',
              pr: 3,
              pl: 3,
              borderRadius: isExpanded ? '16px 16px 0 0' : '16px',
              '& .MuiAccordionSummary-content': {
                margin: '0',
              },

              '&.Mui-focusVisible': {
                backgroundColor: '#fff',
              },
            }}
            expandIcon={
              <Avatar
                tabIndex={0}
                sx={
                  isExpanded
                    ? {
                        width: isDesktop ? '36px' : '24px',
                        height: isDesktop ? '36px' : '24px',
                        transform: 'rotate(180deg)',
                      }
                    : {
                        width: isDesktop ? '40px' : '24px',
                        height: isDesktop ? '40px' : '24px',
                        transform: 'rotate(0deg)',
                      }
                }
                src={
                  isExpanded ? AccordionArrowPurpleClose : AccordionArrowPurple
                }
                alt=''
              />
            }
          >
            <ListItemIcon
              onClick={handleToggle}
              sx={{ minWidth: '40px', alignItems: 'start' }}
            >
              <Checkbox
                edge='start'
                checked={checked}
                inputProps={{
                  id: `check-item-${index}`,
                  'aria-label': `${dictionary.chooseItem} ${item.itemName}`,
                }}
                sx={{
                  '&.MuiCheckbox-root': {
                    pt: '2px',
                  },
                }}
                icon={
                  <Avatar
                    alt=''
                    src={PurpleUnchecked}
                    sx={{ width: '24px', height: '24px' }}
                  />
                }
                checkedIcon={
                  <Avatar
                    alt=''
                    src={PurpleChecked}
                    sx={{ width: '24px', height: '24px' }}
                  />
                }
              />
            </ListItemIcon>

            <Grid
              container
              item
              sx={{ maxWidth: '300px', alignItems: 'center' }}
            >
              <Typography variant='body4'>{item.itemName}</Typography>
            </Grid>

            <Divider
              orientation='vertical'
              variant='middle'
              flexItem
              sx={{
                m: '0 50px',
                height: '48px',
                alignSelf: 'center',
                borderColor: '#EDF2FB',
              }}
              aria-hidden='true'
            />
            <Grid
              item
              container
              justifyContent='flex-start'
              alignItems='center'
            >
              <IconButton
                sx={{
                  width: '40px',
                  height: '40px',
                }}
                onClick={(e) => {
                  handleIncreaseQuantity(e);
                  handleCheckItem(true);
                }}
                aria-label={dictionary.increaseQuantity}
              >
                <Avatar
                  src={PlusPurple}
                  sx={{ width: '32px', height: '32px' }}
                  alt=''
                />
              </IconButton>

              <TextField
                name={`item-quantity-field-${index}`}
                id={`item-quantity-field-${index}`}
                value={equipmentItem.quantity}
                onChange={handleChangeQuantity}
                onFocus={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCheckItem(true);
                }}
                label={dictionary.quantity}
                variant='outlined'
                type={'number'}
                inputProps={{
                  pattern: '[0-9]*',
                  min: 0,
                  style: { padding: '0 20px', height: '40px' },
                }}
                sx={{
                  maxWidth: '100px',
                  p: 0,
                  margin: '0 20px',
                  borderRadius: '6px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '6px',
                    border: '1px solid rgba(23, 82, 175, 0.2)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '6px',
                    border: '1px solid rgba(23, 82, 175, 0.2)',
                  },
                }}
              />

              <IconButton
                sx={{
                  width: '40px',
                  height: '40px',
                }}
                onClick={(e) => {
                  handleDecreaseQuantity(e);
                  handleCheckItem(true);
                }}
                aria-label={dictionary.decreaseQuantity}
              >
                <Avatar
                  src={MinusPurple}
                  sx={{ width: '32px', height: '32px' }}
                  alt=''
                />
              </IconButton>
            </Grid>
          </AccordionSummary>
          <Details
            equipmentItem={equipmentItem}
            index={index}
            item={item}
            period={period}
            handleCheckItem={handleCheckItem}
          />
        </Accordion>
      ) : (
        // MOBILE UI
        <Accordion
          onClick={() => setIsExpanded(!isExpanded)}
          expanded={isExpanded}
          sx={{
            boxShadow: '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)',
            borderRadius: '16px !important',
            border: '1px solid',
            borderColor: 'primary.shade_200',
            width: '100%',
          }}
          disableGutters
        >
          <AccordionSummary
            onClick={() => handleCheckItem(true)}
            expandIcon={
              <Avatar
                tabIndex={0}
                sx={
                  isExpanded
                    ? {
                        width: isDesktop ? '36px' : '24px',
                        height: isDesktop ? '36px' : '24px',
                        transform: 'rotate(180deg)',
                        top: '-8px',
                      }
                    : {
                        width: isDesktop ? '40px' : '32px',
                        height: isDesktop ? '40px' : '32px',
                        transform: 'rotate(0deg)',
                        top: '3px',
                        left: '4px',
                      }
                }
                src={
                  isExpanded ? AccordionArrowPurpleClose : AccordionArrowPurple
                }
                alt=''
              />
            }
            sx={{
              pt: 3,
              pb: '21px',
              pr: 2,
              pl: 2,
              borderRadius: isExpanded ? '16px 16px 0 0' : '16px',
              '& .MuiAccordionSummary-content': {
                margin: '0',
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                alignSelf: 'start',
                mt: '-5px',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'inline-flex' }}>
                <ListItemIcon onClick={handleToggle} sx={{ minWidth: '40px' }}>
                  <Checkbox
                    edge='start'
                    checked={checked}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ id: `check-item-${index}` }}
                    sx={{
                      '&.MuiCheckbox-root': {
                        pt: 0,
                      },
                    }}
                    icon={
                      <Avatar
                        alt=''
                        src={PurpleUnchecked}
                        sx={{ width: '24px', height: '24px' }}
                      />
                    }
                    checkedIcon={
                      <Avatar
                        alt=''
                        src={PurpleChecked}
                        sx={{ width: '24px', height: '24px' }}
                      />
                    }
                  />
                </ListItemIcon>

                <Grid
                  container
                  item
                  sx={{ maxWidth: '300px', alignItems: 'start' }}
                >
                  <Typography variant='body4'>{item.itemName}</Typography>
                </Grid>
              </Box>

              <Divider
                aria-hidden='true'
                orientation='horizontal'
                variant='middle'
                flexItem
                sx={{
                  m: '10px',
                  height: '1px',
                  width: '110%',
                  alignSelf: 'center',
                  borderColor: '#EDF2FB',
                  mb: '24px',
                  right: '-15px',
                  position: 'relative',
                }}
              />
              <Grid
                container
                direction='row'
                justifyContent='flex-start'
                alignItems='center'
              >
                <IconButton
                  sx={{
                    width: '40px',
                    height: '40px',
                  }}
                  onClick={(e) => {
                    handleIncreaseQuantity(e);
                    handleCheckItem(true);
                  }}
                >
                  <Avatar
                    src={PlusPurple}
                    sx={{ width: '32px', height: '32px' }}
                    alt=''
                  />
                </IconButton>

                <TextField
                  name={`item-quantity-field-${index}`}
                  id={`item-quantity-field-${index}`}
                  value={equipmentItem.quantity}
                  onChange={handleChangeQuantity}
                  onFocus={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCheckItem(true);
                  }}
                  label={dictionary.quantity}
                  variant='outlined'
                  inputProps={{
                    pattern: '[0-9]*',
                    min: 0,
                    style: { padding: '0 20px', height: '40px' },
                  }}
                  sx={{
                    maxWidth: '100px',
                    p: 0,
                    margin: '0 10px',
                    borderRadius: '6px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '6px',
                      border: '1px solid rgba(23, 82, 175, 0.2)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '6px',
                      border: '1px solid rgba(23, 82, 175, 0.2)',
                    },
                  }}
                />

                <IconButton
                  sx={{
                    width: '40px',
                    height: '40px',
                  }}
                  onClick={(e) => {
                    handleDecreaseQuantity(e);
                    handleCheckItem(true);
                  }}
                >
                  <Avatar
                    src={MinusPurple}
                    sx={{ width: '32px', height: '32px' }}
                    alt=''
                  />
                </IconButton>
              </Grid>
            </Box>
          </AccordionSummary>
          <Details
            equipmentItem={equipmentItem}
            index={index}
            item={item}
            period={period}
            handleCheckItem={handleCheckItem}
          />
        </Accordion>
      )}
    </ListItem>
  );
};
